import {
  GridCallbackDetails,
  GridColumns,
  GridFilterModel,
  GridInputSelectionModel,
  GridRowParams,
  GridSelectionModel,
  GridSortItem,
  MuiEvent,
} from '@mui/x-data-grid';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Components } from './styled';
import { ProjectTableDataType, ProjectTableType } from './types';
import * as React$1 from 'react';
import { TABLE_DEFAULT_SHOWN_ITEMS, TABLE_HEADER_HEIGHT, TABLE_ROW_HEIGHT } from './constants';
import Placeholder from '../Placeholder';
import { DEFAULT_PLACEHOLDER_TEXT } from '../Placeholder/constants';
import { parseDataToRows } from './utils';
import { ReactComponent as NavigationArrowIcon } from '../../assets/icons/icon-arrow-right.svg';

const ProjectTable = ({
  data,
  columns,
  checkboxSelection = false,
  alternativeBackground = false,
  onSelectionChange,
  selectedRows,
  placeholder = DEFAULT_PLACEHOLDER_TEXT,
  filteringModel,
  sortingModel,
  resetPageTrigger,
  customPageSize,
  showRowCountText,
}: {
  data: ProjectTableDataType;
  columns: {
    columns: GridColumns;
    type: ProjectTableType;
    primaryAction?: (
      params: GridRowParams,
      event: MuiEvent<React$1.SyntheticEvent>,
      details: GridCallbackDetails,
    ) => void;
  };
  checkboxSelection?: boolean;
  alternativeBackground?: boolean;
  onSelectionChange?: (selectionModel: GridSelectionModel) => void;
  selectedRows?: GridInputSelectionModel;
  placeholder?: string;
  filteringModel?: GridFilterModel;
  sortingModel?: { field: string; sort: string };
  resetPageTrigger?: string;
  customPageSize?: number;
  showRowCountText?: string;
}) => {
  const { columns: tableColumns, type: tableType, primaryAction } = columns;
  const tableRows = parseDataToRows(tableType, data);
  const maxPageSize = customPageSize ? customPageSize : TABLE_DEFAULT_SHOWN_ITEMS;

  const [pageSize, setPageSize] = useState(maxPageSize);
  const [pageNavText, setPageNavText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(Math.ceil(tableRows?.length / pageSize));
  const [rowsCount, setRowsCount] = useState(tableRows?.length);

  useEffect(() => {
    if (tableRows?.length > 0) {
      setPageSize(maxPageSize <= tableRows?.length ? maxPageSize : tableRows?.length);
    }
    if (tableRows?.length <= 10) {
      setCurrentPage(1);
    }
  }, [tableRows.length]);

  useEffect(() => {
    if (resetPageTrigger) {
      setCurrentPage(1);
    }
  }, [resetPageTrigger]);

  const onNavInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if ((event.target.value && event.target.value.length <= 2) || event.target.value.length === 0) {
      setPageNavText(event.target.value);
    }
  };
  const onNavGoPress = () => {
    const parsedPageNumber = parseInt(pageNavText);
    if (parsedPageNumber <= Math.ceil(tableRows?.length / pageSize)) {
      setCurrentPage && setCurrentPage(parsedPageNumber);
      setPageNavText('');
    }
  };

  const onPaginationChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage && setCurrentPage(page);
  };

  const _onSelectionChange = (selectionModel: GridSelectionModel) => {
    onSelectionChange?.(selectionModel);
  };

  const _onStateChange = (state: any) => {
    const rows = state.pagination.rowCount;
    setPageCount(Math.ceil(rows / pageSize));
    setRowsCount(rows);
  };

  const onRowDoubleClick = (
    params: GridRowParams,
    event: MuiEvent<React$1.SyntheticEvent>,
    details: GridCallbackDetails,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    primaryAction?.(params, event, details);
  };

  return (
    <Components.Container>
      {showRowCountText && (
        <Components.ItemsCountWrapper>
          <Components.ItemsCountText>
            {showRowCountText}: {rowsCount}
          </Components.ItemsCountText>
        </Components.ItemsCountWrapper>
      )}

      <Components.TableWrapper>
        <Components.Table
          rows={tableRows}
          columns={tableColumns}
          checkboxSelection={checkboxSelection}
          onSelectionModelChange={_onSelectionChange}
          onStateChange={_onStateChange}
          selectionModel={selectedRows}
          filterModel={filteringModel}
          disableColumnMenu
          rowHeight={TABLE_ROW_HEIGHT}
          pageSize={pageSize}
          pagination={undefined}
          headerHeight={TABLE_HEADER_HEIGHT}
          page={(currentPage ? currentPage : 2) - 1}
          disableColumnSelector
          disableColumnFilter
          onRowDoubleClick={onRowDoubleClick}
          autoHeight
          hideFooter
          initialState={{ sorting: { sortModel: !!sortingModel ? [sortingModel as GridSortItem] : [] } }}
          components={{
            NoRowsOverlay: () => (
              <Components.PlaceholderWrapper>
                <Placeholder type={'fullHeight'} text={placeholder} />
              </Components.PlaceholderWrapper>
            ),
            NoResultsOverlay: () => (
              <Components.PlaceholderWrapper>
                <Placeholder type={'fullHeight'} text={placeholder} />
              </Components.PlaceholderWrapper>
            ),
          }}
        />
      </Components.TableWrapper>
      {pageCount > 1 && (
        <Components.TablePaginationContainer alternativeBackground={alternativeBackground}>
          <Components.PaginationRightContainer>
            <Components.TablePagination
              page={currentPage}
              variant={'outlined'}
              count={pageCount}
              onChange={onPaginationChange}
            />
            {pageCount > 1 && (
              <>
                <Components.NavigationSeparator />
                <Components.PaginationNavigation>
                  <Components.NavigationText>Go to page</Components.NavigationText>
                  <Components.NavigationInput
                    value={pageNavText}
                    type={'number'}
                    variant={'outlined'}
                    size={'small'}
                    onChange={onNavInputChange}
                  />
                  <Components.PaginationNavigationButton onClick={onNavGoPress}>
                    <Components.PaginationNavigationButtonText>Go</Components.PaginationNavigationButtonText>
                    <Components.NavigationIconWrapper>
                      <NavigationArrowIcon />
                    </Components.NavigationIconWrapper>
                  </Components.PaginationNavigationButton>
                </Components.PaginationNavigation>
              </>
            )}
          </Components.PaginationRightContainer>
        </Components.TablePaginationContainer>
      )}
    </Components.Container>
  );
};

export default ProjectTable;
